// src/hooks/usePopup.js
/**
 * Simple React hook for controlling a single, app-wide popup or modal.
 *
 * @example
 *   const { popup, open, close } = usePopup();
 *   open({ message: 'Something went wrong.' });
 *
 * `popup` is either `null` or an object with the following shape:
 *   {
 *     message: string,
 *     onConfirm?: () => (void|Promise<void>)
 *   }
 *
 * The hook does **not** prescribe any UI; pair it with a <Popup /> component
 * that accepts `{ popup, onClose }`.
 */

import { useState, useCallback } from "react";

/**
 * @returns {{
 *   popup: { message: string, onConfirm?: Function } | null,
 *   open:  (cfg: { message: string, onConfirm?: Function }) => void,
 *   close: () => void
 * }}
 */
export default function usePopup() {
  const [popup, setPopup] = useState(null);

  /** Opens a popup with the provided configuration. */
  const open = useCallback((cfg) => {
    setPopup(cfg);
  }, []);

  /** Closes the currently open popup. */
  const close = useCallback(() => setPopup(null), []);

  return { popup, open, close };
}
